import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { ATOL_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import { useContext, useMemo, useCallback } from 'react'
import * as React from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { styled } from 'styled-components'
import type { NavUspType } from './NavBar/NavBarUSP'
import NavBarUSP from './NavBar/NavBarUSP'
import NavBarChatButton from './NavBarButtons/NavBarChatButton'
import NavBarContactsButton from './NavBarButtons/NavBarContactsButton'
import NavBarStoreButton from './NavBarButtons/NavBarStoreButton'
import NavBarWhatsAppChannelButton from './NavBarButtons/NavBarWhatsAppChannelButton'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Pane from 'components/Common/Pane'
import switchRegion from 'lib/geo/switchRegion'
import TextLink from 'LuxKit/TextLink'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import Flag from 'LuxKit/Flag'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import LoyaltyInfiniteLogo from 'loyalty/components/LoyaltyInfiniteLogo'
import config from 'constants/config'
import { FormattedMessage, useIntl } from 'react-intl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { useLocation } from 'react-router-dom'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'

const Container = styled(Pane)`
  width: 100%;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const TertiaryBar = styled(LayoutContainer)`
  display: flex;
  justify-content: center;
  padding: 0 ${rem(16)};

  ${mediaQueryUp.desktop} {
    justify-content: space-between;
    padding: 0 ${rem(20)};
  }
`

const USPSection = styled(Group)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
  }
`

const DelimiterBullet = styled(BodyText)`
  &:first-child { display: none; }
`

const LoyaltyTierDisplay = styled(Caption)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};

  > button {
    display: inline-flex;
    align-items: center;
    line-height: 0;
  }
`

const InfiniteBarLogo = styled(LoyaltyInfiniteLogo)`
  height: ${rem(10)};
  width: auto;
`

interface Props {
  className?: string
}

function NavBarTertiaryBar(props: Props) {
  const { className } = props
  const intl = useIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  const { currentRegionCode } = useContext(GeoContext)

  const chatEnabled = useAppSelector(isSupportAssistantEnabled)
  const loggedIn = useAppSelector(selectLoggedIn)
  const usps = useMemo<Array<NavUspType>>(() => {
    const usps: Array<NavUspType> = loggedIn ? [] : ['google']
    if (ATOL_REGIONS.includes(currentRegionCode)) {
      usps.unshift('atol')
    }
    return usps
  }, [currentRegionCode, loggedIn])

  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount) && !isAgentHubEnabled
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const [tier, tierName] = useCurrentLoyaltyTier()

  const renderTierLabel = useCallback((chunks: React.ReactNode) => {
    if (isInfiniteTier) return <InfiniteBarLogo role="img" aria-label={tierName} />
    return <Caption variant="large" format="uppercase" weight="bold" as="span">{chunks}</Caption>
  }, [isInfiniteTier, tierName])
  const loyaltyAccountNumber = useAppSelector(state => state.loyalty.account.data?.accountNumber)
  const membershipNumber = loyaltyAccountNumber != null ? `${loyaltyAccountNumber}` : undefined
  const loyaltyRegion = useAppSelector(getLoyaltyAccountRegion)
  const loyaltyCurrency = useMemo(() => {
    return config.regions.find(r => r.code === loyaltyRegion)?.currencyCode
  }, [loyaltyRegion])
  const isLoyaltyAccountInCorrectRegion = loyaltyRegion === currentRegionCode
  const showRegionSwitch = useMemo(() => {
    return loggedIn && !isLoyaltyAccountInCorrectRegion
  }, [loggedIn, isLoyaltyAccountInCorrectRegion])
  const handleSwitchRegion = useCallback(() => {
    if (!loyaltyRegion) return
    switchRegion(loyaltyRegion)
  }, [loyaltyRegion])

  const membershipLabel = intl.formatMessage(
    {
      id: 'u3b6a14bkUpk9MT1zrL8',
      defaultMessage: 'Membership {loyaltyAccountNumber}',
      description: 'Loyalty membership number display in tertiary navigation bar',
    },
    { loyaltyAccountNumber },
  )
  const copyMembershipNumberLabel = intl.formatMessage({
    id: 'gkoIBvPTDxg5zeyp1z9S',
    defaultMessage: 'Copy membership number',
    description: 'Aria label for button to copy loyalty membership number',
  })
  const programName = getLoyaltyProgramDisplayName()
  const regionSwitchProgramName = isInfiniteTier ? 'Infinite' : programName

  const { pathname } = useLocation()
  const isPurchasePointsPage = !!matchLocaleAwarePath(pathname, { path: `/:regionCode/${LOYALTY_PURCHASE_POINTS_PAGE}` })

  return <ProductPaletteProvider palette={hasLoyaltyAccount ? `tier-${tier}` : 'default'}>
    <Container
      className={className}
      background={hasLoyaltyAccount ? `tier-${tier}-primary` : 'light-background'}
    >
      <TertiaryBar variant="extraLarge">
        <USPSection direction="horizontal" verticalAlign="center" gap={8}>
          {hasLoyaltyAccount &&
            <>
              <LoyaltyTierDisplay variant="large">
                <FormattedMessage
                  id="Zjv4K3WiLH7NxzTLkAtS"
                  defaultMessage="<tier>{tierName}</tier> {separator} {membershipLabel}"
                  description="Tertiary bar loyalty display combining tier name, separator, and membership number"
                  values={{
                    tierName,
                    separator: sep,
                    membershipLabel,
                    tier: renderTierLabel,
                  }}
                />
                {membershipNumber && (
                  <CopyToClipboardClickableIcon
                    value={membershipNumber}
                    ariaLabel={copyMembershipNumberLabel}
                  />
                )}
              </LoyaltyTierDisplay>
              {showRegionSwitch && loyaltyCurrency && <>
                {loyaltyRegion && <Flag size="S" countryCode={loyaltyRegion} />}
                <DelimiterBullet variant="small">{intl.formatMessage(middleDotSeparatorMessage)}</DelimiterBullet>
                <Caption variant="large">
                  {isPurchasePointsPage && <FormattedMessage
                    defaultMessage="To purchase {programName} Points <link>Switch to {loyaltyCurrency} <icon></icon></link>"
                    id="fVLuF7ZKMQCgk9NCv_Nj"
                    description="Prompt on the Buy Points landing page to switch region to the loyalty account currency before purchasing points"
                    values={{
                      programName: regionSwitchProgramName,
                      loyaltyCurrency,
                      link: (chunks: React.ReactNode) => <TextLink onClick={handleSwitchRegion}>{chunks}</TextLink>,
                      icon: () => <LineSyncIcon />,
                    }}
                  />}
                  {!isPurchasePointsPage && <FormattedMessage
                    defaultMessage="To use your {programName} benefits <link>Switch to {loyaltyCurrency} <icon></icon></link>"
                    id="GbqdkXdssgDltWaJAUGH"
                    description="Prompt to switch region with link to switch currency in tertiary bar"
                    values={{
                      programName: regionSwitchProgramName,
                      loyaltyCurrency,
                      link: (chunks: React.ReactNode) => <TextLink onClick={handleSwitchRegion}>{chunks}</TextLink>,
                      icon: () => <LineSyncIcon />,
                    }}
                  />}
                </Caption>
              </>}
            </>}
          {usps.map((usp) => <React.Fragment key={usp}>
            <DelimiterBullet variant="small">{intl.formatMessage(middleDotSeparatorMessage)}</DelimiterBullet>
            <NavBarUSP type={usp} />
          </React.Fragment>)}
        </USPSection>
        <Group direction="horizontal" desktopGap={8}>
          <NavBarContactsButton textBreakpoint={usps.length > 2 ? 'desktop' : undefined} />
          <NavBarWhatsAppChannelButton />
          {chatEnabled && <NavBarChatButton />}
          <NavBarStoreButton />
        </Group>
      </TertiaryBar>
    </Container>
  </ProductPaletteProvider>
}

export default NavBarTertiaryBar
